import {
  ActionIcon,
  Button,
  Card,
  Code,
  Checkbox,
  Group,
  Select,
  Stack,
  TagsInput,
  TextInput,
  Table,
  Title,
} from "@mantine/core";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { useForm } from "@mantine/form";
import { Link } from "wouter";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { getProductType } from "../api";

const PREDEFINED_ROLES = [
  "uniqueIdentifier",
  "productGroup",
  "mainImage",
  "category",
  "brand",
  "name",
];

export function AbstractMappingForm({ marketplaceId, initialData, onSubmit }) {
  const { t } = useTranslation("imports");
  const [baseCategory, setBaseCategory] = useState("");
  const roleOptions = PREDEFINED_ROLES.map((role) => ({
    value: role,
    label: t(`abstractMapping.roles.${role}`),
  }));
  // const [productType, setProductType] = useState(null);

  const initialValues = {
    categoryMapper: initialData?.categoryMapper || "MiraklCategoryPathMapper",
    predefinedMappings: PREDEFINED_ROLES.reduce(
      (acc, role) => ({
        ...acc,
        [role]: initialData?.systemFieldMappings?.[role] || "",
      }),
      {}
    ),
    customMappings:
      initialData?.mappings ||
      [
        // { pattern: "", isCreative: false, neverTouch: false },
      ],
    metaFields:
      initialData?.meta ||
      [
        // { pattern: "", isCreative: false, neverTouch: false },
      ],
  };

  // const initialValues2 = {
  //   categoryMapper: "miraklCategoryPathMapper",
  //   predefinedMappings: { uniqueIdentifier: "EAN" },
  //   customMappings: [],
  //   metaFields: [],
  // };

  const form = useForm({
    initialValues: initialValues,
  });

  const handleSubmit = (values) => {
    onSubmit(values);
  };

  const fetchBaseCategory = () => {
    console.log("fetching base category, baseCategory:", baseCategory);
    getProductType(marketplaceId, baseCategory).then((data) => {
      // setProductType(data.hydratedType);
      // Build a set of all existing tokens (sources + destination) to avoid duplicates
      const used = new Set();
      for (const m of form.values.customMappings) {
        if (Array.isArray(m.sources)) {
          m.sources.filter(Boolean).forEach((s) => used.add(s));
        }
        // Some older entries might have a singular 'source' key
        if (m.source) used.add(m.source);
        if (m.destination) used.add(m.destination);
      }

      const newMappings = data.hydratedType.attributes
        .filter((attr) => {
          const code = attr.code;
          // attr.label might be undefined / null
          const label = attr.label;
          return !used.has(code) && (!label || !used.has(label));
        })
        .map((attr) => ({
          sources: [attr.code, attr.label].filter(Boolean),
          destination: attr.code,
        }));

      form.setValues({
        ...form.values,
        baseCategoryId: baseCategory,
        customMappings: [...form.values.customMappings, ...newMappings],
      });
    });
  };

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      {/* <Code block>{JSON.stringify(initialValues2, null, 2)}</Code>
      <Code block>{JSON.stringify(initialValues, null, 2)}</Code> */}

      <Stack spacing="lg">
        <Card withBorder>
          <Title order={3} mb="md">
            {t("abstractMapping.selectBaseCategory")}
          </Title>
          <Link target="_blank" to="/product-types/tree">
            {t("abstractMapping.browseTypes")}
          </Link>
          <Group align="flex-end">
            <TextInput
              label={t("abstractMapping.baseCategory")}
              value={baseCategory}
              onChange={(event) => setBaseCategory(event.currentTarget.value)}
            />
            <Button onClick={() => fetchBaseCategory()}>
              {t("abstractMapping.fetch")}
            </Button>
          </Group>
        </Card>

        <Select
          label={t("abstractMapping.categoryMapper")}
          {...form.getInputProps("categoryMapper")}
          data={["miraklCategoryPathMapper", "miraklCategoryCodeMapper"]}
        />
        {/* 
        <Card withBorder>
          <Title order={3} mb="md">
            Predefined Mappings
          </Title>
          <Stack>
            {PREDEFINED_ROLES.map((role) => (
              <TextInput
                key={form.key(`predefinedMappings.${role}`)}
                label={role}
                placeholder="Enter regex pattern"
                {...form.getInputProps(`predefinedMappings.${role}`)}
              />
            ))}
          </Stack>
        </Card> */}

        <Card withBorder>
          <Title order={3} mb="md">
            {t("abstractMapping.customMappings")}
          </Title>
          <Table
            stickyHeader
            stickyHeaderOffset={30}
            striped
            highlightOnHover
            withRowBorders={false}
          >
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("abstractMapping.sources")}</Table.Th>
                <Table.Th>{t("common:generic.role")}</Table.Th>
                <Table.Th>{t("abstractMapping.destination")}</Table.Th>
                <Table.Th>{t("abstractMapping.creative")}</Table.Th>
                <Table.Th>{t("abstractMapping.noTouch")}</Table.Th>
                <Table.Th>{t("abstractMapping.scored")}</Table.Th>
                <Table.Th></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {form.values.customMappings.map((_, index) => (
                <Table.Tr key={index}>
                  <Table.Td>
                    <TagsInput
                      {...form.getInputProps(`customMappings.${index}.sources`)}
                    />
                  </Table.Td>
                  <Table.Td>
                    <Select
                      data={roleOptions}
                      {...form.getInputProps(`customMappings.${index}.role`)}
                    />
                  </Table.Td>
                  <Table.Td>
                    <TextInput
                      placeholder={t("abstractMapping.regexPlaceholder")}
                      {...form.getInputProps(
                        `customMappings.${index}.destination`
                      )}
                    />
                  </Table.Td>
                  <Table.Td>
                    <Checkbox
                      {...form.getInputProps(
                        `customMappings.${index}.isCreative`,
                        {
                          type: "checkbox",
                        }
                      )}
                    />
                  </Table.Td>
                  <Table.Td>
                    <Checkbox
                      {...form.getInputProps(
                        `customMappings.${index}.doNotAskAi`,
                        {
                          type: "checkbox",
                        }
                      )}
                    />
                  </Table.Td>
                  <Table.Td>
                    <Checkbox
                      {...form.getInputProps(
                        `customMappings.${index}.isScored`,
                        {
                          type: "checkbox",
                        }
                      )}
                    />
                  </Table.Td>
                  <Table.Td>
                    <ActionIcon
                      color="red"
                      variant="subtle"
                      onClick={() =>
                        form.removeListItem("customMappings", index)
                      }
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>

          <Button
            leftSection={<IconPlus size={16} />}
            variant="light"
            onClick={() =>
              form.insertListItem("customMappings", {
                source: "",
                destination: "",
                isCreative: false,
                neverTouch: false,
              })
            }
          >
            {t("abstractMapping.addCustomMapping")}
          </Button>
        </Card>

        <Card withBorder>
          <Title order={3} mb="md">
            {t("abstractMapping.metaFields")}
          </Title>
          <Stack spacing="md">
            {form.values.metaFields.map((_, index) => (
              <Group key={index} align="flex-end">
                <TextInput
                  label={t("abstractMapping.regexPattern")}
                  placeholder={t("abstractMapping.regexPlaceholder")}
                  {...form.getInputProps(`metaFields.${index}.source`)}
                />

                <Checkbox
                  label={t("abstractMapping.isCreative")}
                  {...form.getInputProps(`metaFields.${index}.isCreative`, {
                    type: "checkbox",
                  })}
                />
                <Checkbox
                  label={t("abstractMapping.neverTouch")}
                  {...form.getInputProps(`metaFields.${index}.neverTouch`, {
                    type: "checkbox",
                  })}
                />

                <ActionIcon
                  color="red"
                  onClick={() => form.removeListItem("metaFields", index)}
                >
                  <IconTrash size={16} />
                </ActionIcon>
              </Group>
            ))}
            <Button
              leftSection={<IconPlus size={16} />}
              variant="light"
              onClick={() =>
                form.insertListItem("metaFields", {
                  source: "",
                  isCreative: false,
                  neverTouch: false,
                })
              }
            >
              {t("abstractMapping.addMetaField")}
            </Button>
          </Stack>
        </Card>

        <Group position="right">
          <Button type="submit">{t("abstractMapping.saveChanges")}</Button>
        </Group>
      </Stack>
    </form>
  );
}
